import { FaSave } from 'react-icons/fa';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import useFetch from 'use-http';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '../../../hooks/AuthContext';
import { InsertPasswordMessage, PasswordContainer } from './styles';

export const ModalFirstAccess = (): JSX.Element => {
  const [userChangedHisPassword, setUserChangedHisPassword] = useState(false);
  const [animation, setAnimation] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('Seja bem vindo.');

  const { put, response, loading } = useFetch();
  const { modalFirstAccessStatus, setModalFirstAccessStatus, authData } =
    useAuth();

  const handleSubmitNewPassword = useCallback(
    async (values: { password: string; password_confirmation: string }) => {
      await put(`/auth/users/change-password`, values);
      if (response.ok) {
        toast.success('Senha atualizada com sucesso!');
      } else {
        toast.error(
          'Ocorreu um erro interno. Tente alterar sua senha mais tarde!',
        );
      }
      setModalFirstAccessStatus(false);
      setUserChangedHisPassword(true);
    },
    [put, response.ok, setModalFirstAccessStatus],
  );

  const changePasswordForm = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: {
      password: '',
      password_confirmation: '',
    },
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .required('Senha é obrigatória')
        .min(8, 'Mínimo 8 caracteres'),
      password_confirmation: Yup.string()
        .required('Confirmação de senha é obrigatória')
        .oneOf([Yup.ref('password'), undefined], 'Confirmação incorreta'),
    }),
    onSubmit: async values => {
      await handleSubmitNewPassword(values);
    },
  });

  const handleClose = useCallback(() => {
    if (userChangedHisPassword) {
      setModalFirstAccessStatus(false);
    } else {
      setAnimation(true);
    }
  }, [userChangedHisPassword, setModalFirstAccessStatus]);

  useEffect(() => {
    if (authData.requires_password_change) {
      if (authData.name) {
        const firstName = authData.name.split(' ').at(0) ?? '';
        if (firstName?.length > 0)
          setWelcomeMessage(`Seja bem vindo, ${firstName}.`);
      }
      setModalFirstAccessStatus(true);
    }
  }, [
    authData.requires_password_change,
    authData.name,
    setModalFirstAccessStatus,
  ]);

  return (
    <Dialog open={modalFirstAccessStatus} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl">
            {welcomeMessage}
          </DialogTitle>
        </DialogHeader>

        {!loading ? (
          <>
            <p className="text-center">
              Este é seu primeiro acesso.
              <br />
            </p>
            <InsertPasswordMessage
              animation={animation}
              onAnimationEnd={() => {
                setAnimation(false);
              }}
            >
              Para continuar, preencha uma nova senha:
            </InsertPasswordMessage>
            <form
              onSubmit={changePasswordForm.handleSubmit}
              className="space-y-4"
            >
              <PasswordContainer>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Digite a nova senha"
                    value={changePasswordForm.values.password}
                    onChange={changePasswordForm.handleChange}
                    onBlur={changePasswordForm.handleBlur}
                  />
                  {changePasswordForm.touched.password &&
                    changePasswordForm.errors.password && (
                      <p className="text-sm text-destructive">
                        {changePasswordForm.errors.password}
                      </p>
                    )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password_confirmation">Confirmar senha</Label>
                  <Input
                    id="password_confirmation"
                    name="password_confirmation"
                    type="password"
                    placeholder="Digite novamente a senha"
                    value={changePasswordForm.values.password_confirmation}
                    onChange={changePasswordForm.handleChange}
                    onBlur={changePasswordForm.handleBlur}
                  />
                  {changePasswordForm.touched.password_confirmation &&
                    changePasswordForm.errors.password_confirmation && (
                      <p className="text-sm text-destructive">
                        {changePasswordForm.errors.password_confirmation}
                      </p>
                    )}
                </div>
              </PasswordContainer>

              <DialogFooter>
                <Button type="submit" className="w-full">
                  <FaSave className="mr-2" /> Salvar
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div className="space-y-4 py-8">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ModalFirstAccess;
